/**
 * Practice domains — which areas of work a user is involved in.
 *
 * A domain is a practice area of the platform: `physical` (strength, running,
 * conditioning, ...) and `shooting` (dry fire, live fire, scores). It decides
 * which surfaces of the interface are composed for a user — a coach who only
 * teaches shooting must not be shown the physical-prep builder, and an athlete
 * must never be offered sections of a domain they do not practise.
 *
 * The domains are resolved **server-side** (`backend/services/domains.py`) and
 * arrive on the auth payload as `user.domains`, a concrete list. This module
 * never re-derives them from `sport_disciplines`: one resolver, one answer.
 *
 * Two rules that are easy to get wrong:
 *
 *   1. **Gate rendering and creation, never data.** A coach without the
 *      `physical` domain who opens a shared program full of strength exercises
 *      must still read it, with labels and icons resolved. Keep taxonomy
 *      constants (e.g. `EXERCISE_TYPES`) complete and filter at the point of
 *      rendering the picker — never amputate the constant itself.
 *   2. **Missing means permitted.** An absent or empty `domains` is treated as
 *      "no restriction", which covers the deploy window where a still-valid
 *      token predates the field.
 *
 * Spec: docs/shooting/07-domini-e-abilitazioni.md
 */

export const DOMAIN_PHYSICAL = "physical";
export const DOMAIN_SHOOTING = "shooting";

/**
 * True when `user` may be shown the surfaces of `domain`.
 * @param {object|null|undefined} user - the auth user (from AuthContext)
 * @param {string} domain - DOMAIN_PHYSICAL | DOMAIN_SHOOTING
 */
export function hasDomain(user, domain) {
	if (!user || !domain) return false;
	if (user.is_admin) return true;
	const domains = user.domains;
	// Absent or empty => unrestricted, see rule 2 above.
	if (!Array.isArray(domains) || domains.length === 0) return true;
	return domains.includes(domain);
}

/** Convenience wrappers — the two domains that exist today. */
export const hasPhysicalDomain = (user) => hasDomain(user, DOMAIN_PHYSICAL);
export const hasShootingDomain = (user) => hasDomain(user, DOMAIN_SHOOTING);

/**
 * Filter a list of taxonomy entries by their optional `domain` key.
 * Entries without a `domain` belong to every user.
 *
 * Use at the point of rendering a picker, never to rebuild the source
 * constant — see rule 1 above.
 */
export function filterByDomain(items, user) {
	if (!Array.isArray(items)) return [];
	return items.filter((item) => !item?.domain || hasDomain(user, item.domain));
}
